import React, { useEffect } from 'react';
import { Provider } from 'react-redux';
import _pick from 'lodash/pick';
import type { AppProps, NextWebVitalsMetric } from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { PageProps } from 'types';

import ModalContainer, { ModalContainerPropFields } from 'components/ModalContainer';
import { HistoryProvider } from 'hooks';
import { jobService } from 'services/jobService';
import { jobListingHash } from 'store/jobs/actions';
import { dispatchOpenDLPModal } from 'store/modals/actions';
import HeaderLogoOnly from 'widgets/header/HeaderLogoOnly';
import HeaderWithProgressBar from 'widgets/header/HeaderWithProgressBar';
import HeaderWithSteps from 'widgets/header/headerWithSteps';

import '../../utils/nativeUtils';

import { useStore } from '../store';
import { checkLocalStorage, loadServiceWorker, sendAnalytics } from '../utils';
import { GOOGLE_AD_SENSE_PUBLIC_KEY, HIDE_GOOGLE_ADS_ROUTES } from '../utils/constant';
import Header from '../widgets/header';

import 'react-toastify/dist/ReactToastify.css';
import '../css/application.css';
import '../less/less.css';
import '../components/index.css';
import '../layouts/index.css';
import '../widgets/index.css';
import 'styles/app.global.scss';

const {
  publicRuntimeConfig: { GTM_ID, MEASUREMENT_ID },
} = getConfig();

const Footer = dynamic(() => import('../widgets/Footer'));

declare global {
  interface Window {
    userAgent: string;
    pageCategory: string;
    userId: string;
    isLoggedIn: boolean;
    userGroup: string;
    GA_INITIALIZED: boolean;
    btoa: (value: string) => string;
  }
}

if (!global.window) {
  global.window = {
    navigator: {
      userAgent: 'node',
    },
    userAgent: 'node',
    location: {
      protocol: 'http:',
      hostname: 'localhost',
      port: '8000',
    },
  } as Window & typeof globalThis;
}

if (typeof document === 'undefined') {
  global.document = {
    querySelector: () => null,
  } as unknown as Document;
}

window.btoa = (str) => Buffer.from(str).toString('base64');

export function reportWebVitals(metric: NextWebVitalsMetric) {
  switch (metric.name) {
    case 'FCP':
      sendAnalytics(metric);
      break;
    case 'LCP':
      sendAnalytics(metric);
      break;
    case 'CLS':
      sendAnalytics(metric);
      break;
    case 'FID':
      sendAnalytics(metric);
      break;
    case 'TTFB':
      sendAnalytics(metric);
      break;
    case 'Next.js-hydration':
      sendAnalytics(metric);
      break;
    case 'Next.js-route-change-to-render':
      sendAnalytics(metric);
      break;
    case 'Next.js-render':
      sendAnalytics(metric);
      break;
    default:
      break;
  }
}

export default function App({
  Component,
  pageProps,
}: AppProps<
  PageProps<{
    resumeData?: Record<string, unknown>;
    stripeSubscriptionData?: Record<string, unknown>;
    stickyHeader?: boolean;
    isHomePage?: boolean;
    [prop: string]: any;
  }>
>) {
  const router = useRouter();
  const store = useStore(pageProps, router);

  checkLocalStorage();

  useEffect(() => {
    async function fetchListingHashes() {
      try {
        const listingHashes = await jobService.getUserlistinghashes();
        store.dispatch(jobListingHash(listingHashes));
      } catch (err) {
        console.error('Error occurred while fetching listing hashes');
      }
    }

    function openLastDLPAfterSignIn() {
      const job = window.localStorage.getItem('dlpJobBeforeSignIn');

      if (job) {
        dispatchOpenDLPModal(JSON.parse(job), window.pageCategory)(store.dispatch, store.getState);
        window.localStorage.removeItem('dlpJobBeforeSignIn');
      }
    }

    fetchListingHashes();

    openLastDLPAfterSignIn();

    window.addEventListener('load', loadServiceWorker);
    return () => {
      window.removeEventListener('load', loadServiceWorker);
    };
  }, [store]);

  const hideGoogleAd = HIDE_GOOGLE_ADS_ROUTES.some((route) => router?.asPath?.startsWith(route));

  const hideFooter = pageProps?.data?.notShowFooter || pageProps?.data?.notShowHeaderAndFooter;

  const getHeaderComponent = () => {
    if (pageProps?.data?.showHeaderOnlyLogo) {
      return <HeaderLogoOnly />;
    }

    if (pageProps?.data?.showHeaderWithSteps) {
      return (
        <HeaderWithSteps
          locals={pageProps.locals}
          criteria={pageProps.data.criteria}
          metadata={pageProps.data.metadata}
          steps={pageProps.data.headerSteps?.steps || []}
          currentStep={pageProps.data.headerSteps?.currentStep || ''}
        />
      );
    }

    if (pageProps?.data?.showHeaderWithProgressBar) {
      return <HeaderWithProgressBar stickyHeader={pageProps?.data?.stickyHeader} />;
    }

    if (pageProps?.data?.notShowHeaderAndFooter) {
      return null;
    }

    return <Header {...pageProps.data} />;
  };

  return (
    <>
      <HistoryProvider>
        <Provider store={store}>
          <Head>
            <meta
              name="viewport"
              content="height=device-height, width=device-width, initial-scale=1.0, viewport-fit=cover"
            />
          </Head>

          {getHeaderComponent()}

          {!pageProps?.data?.noModals && (
            <ModalContainer {..._pick(pageProps.data, ModalContainerPropFields)} />
          )}

          <Component {...pageProps} />

          {pageProps.data && !hideFooter && (
            <Footer
              showSalesContactInfo={pageProps.data?.showSalesContactInfo}
              hideFooterAds={pageProps.data?.hideFooterAds}
              isHomePage={pageProps.data?.isHomePage}
            />
          )}
        </Provider>
      </HistoryProvider>
      {!hideGoogleAd && (
        <Script
          async
          id="Adsense-id"
          crossOrigin="anonymous"
          strategy="lazyOnload"
          onError={(e) => {
            console.error('Script failed to load', e);
          }}
          src={`https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${GOOGLE_AD_SENSE_PUBLIC_KEY}`}
        />
      )}
      <Script
        src={`https://www.googletagmanager.com/gtag/js?id=${MEASUREMENT_ID}`}
        strategy="lazyOnload"
      />
      <Script
        id="google-tag-manager"
        src={`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`}
        strategy="lazyOnload"
      />
      <Script
        async
        id="clarity-script"
        dangerouslySetInnerHTML={{
          __html: `(function (c, l, a, r, i, t, y) {
              c[a] =
                c[a] ||
                function () {
                  (c[a].q = c[a].q || []).push(arguments);
                };
              t = l.createElement(r);
              t.async = 1;
              t.src = 'https://www.clarity.ms/tag/' + i;
              y = l.getElementsByTagName(r)[0];
              y.parentNode.insertBefore(t, y);
            })(window, document, 'clarity', 'script', 'cq6d0wri04')`,
        }}
      />
    </>
  );
}
